import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useAuth } from '@/features/auth';
import { router } from '@/router';
import { api } from '@watchparty/shared';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Button } from '@/components/ui/Button';
function getErrorMessage(error) {
    if (error instanceof Error)
        return error.message;
    return 'Something went wrong.';
}
export function OnboardingPage() {
    const navigate = useNavigate();
    const auth = useAuth();
    const isOAuthUser = auth.user?.loginProvider != null;
    const [step, setStep] = useState(isOAuthUser ? 1 : 2);
    const [displayName, setDisplayName] = useState(auth.user?.displayName ?? '');
    const [partyName, setPartyName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [pendingInvite, setPendingInvite] = useState(null);
    useEffect(() => {
        if (displayName) {
            setPartyName(`${displayName}'s Party`);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        const token = sessionStorage.getItem('pendingJoinToken');
        if (!token)
            return;
        api.get(`/parties/invite/${token}`)
            .then(setPendingInvite)
            .catch(() => {
            sessionStorage.removeItem('pendingJoinToken');
        });
    }, []);
    async function handleStep1Submit(e) {
        e?.preventDefault();
        const trimmed = displayName.trim();
        if (!trimmed) {
            setError('Display name is required.');
            return;
        }
        if (trimmed.length > 64) {
            setError('Display name must be 64 characters or fewer.');
            return;
        }
        setError(null);
        setIsSubmitting(true);
        try {
            await api.put('/auth/me', { displayName: trimmed });
            await auth.refreshUser();
            setPartyName(`${trimmed}'s Party`);
            setStep(2);
        }
        catch (err) {
            setError(getErrorMessage(err));
        }
        finally {
            setIsSubmitting(false);
        }
    }
    function handleSkipStep1() {
        setPartyName(displayName ? `${displayName}'s Party` : 'My Party');
        setStep(2);
    }
    async function handleStep2Submit(e) {
        e?.preventDefault();
        const trimmed = partyName.trim();
        if (!trimmed) {
            setError('Party name is required.');
            return;
        }
        setError(null);
        setIsSubmitting(true);
        try {
            const party = await api.post('/parties', { name: trimmed });
            // Refresh user data in background
            auth.refreshUser();
            // Eagerly update router context so route guards see onboardingComplete
            // before React re-renders with the refreshed query data
            router.update({
                context: {
                    auth: {
                        isAuthenticated: true,
                        isAdmin: auth.isAdmin,
                        onboardingComplete: true,
                        userId: auth.user?.userId ?? null,
                        defaultPartyId: party.partyId,
                    },
                },
            });
            const pendingToken = sessionStorage.getItem('pendingJoinToken');
            if (pendingToken) {
                sessionStorage.removeItem('pendingJoinToken');
                try {
                    const accepted = await api.post(`/parties/invite/${pendingToken}/accept`);
                    await navigate({ to: '/parties/$partyId', params: { partyId: accepted.partyId } });
                }
                catch {
                    // Invite expired or invalid — fall back to the newly created party
                    await navigate({ to: '/parties/$partyId', params: { partyId: party.partyId } });
                }
            }
            else {
                await navigate({ to: '/parties/$partyId', params: { partyId: party.partyId } });
            }
        }
        catch (err) {
            setError(getErrorMessage(err));
        }
        finally {
            setIsSubmitting(false);
        }
    }
    async function handleSkipStep2() {
        setIsSubmitting(true);
        try {
            await api.post('/auth/onboarding/complete');
            await auth.refreshUser();
            const pendingToken = sessionStorage.getItem('pendingJoinToken');
            if (pendingToken) {
                sessionStorage.removeItem('pendingJoinToken');
                await navigate({ to: '/join/$token', params: { token: pendingToken } });
            }
            else {
                await navigate({ to: '/parties' });
            }
        }
        catch {
            await navigate({ to: '/parties' });
        }
        finally {
            setIsSubmitting(false);
        }
    }
    return (_jsxs("div", { style: {
            minHeight: '100vh',
            background: 'var(--color-bg)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
        }, children: [_jsx("div", { style: { marginBottom: '24px', textAlign: 'center' }, children: _jsx("span", { style: {
                        fontFamily: 'var(--font-family-heading)',
                        fontSize: '1.125rem', fontWeight: 700,
                        color: 'var(--color-text-muted)',
                        letterSpacing: '-0.01em',
                    }, children: "watchparty" }) }), _jsx("div", { style: { display: 'flex', gap: '8px', justifyContent: 'center', marginBottom: '32px' }, children: [1, 2].map((n) => (_jsx("div", { style: {
                        width: step >= n ? '24px' : '8px',
                        height: '8px',
                        borderRadius: 'var(--radius-full)',
                        background: step >= n ? 'var(--color-text)' : 'var(--color-border-strong)',
                        transition: 'width 200ms ease, background 200ms ease',
                    } }, n))) }), _jsx("div", { style: {
                    width: '100%',
                    maxWidth: '440px',
                    background: 'var(--color-surface)',
                    borderRadius: 'var(--radius-lg)',
                    border: '1px solid var(--color-border)',
                    padding: '40px',
                    boxShadow: 'var(--shadow-lg)',
                }, children: step === 1 ? (_jsxs("form", { onSubmit: handleStep1Submit, children: [_jsx("h1", { style: {
                                fontFamily: 'var(--font-family-heading)',
                                fontSize: '1.5rem', fontWeight: 700,
                                color: 'var(--color-text)', marginBottom: '8px',
                            }, children: "What should we call you?" }), _jsx("p", { style: { fontSize: '0.875rem', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-body)', marginBottom: '24px' }, children: "This is how your friends will see you in Watch Parties." }), _jsxs("div", { style: { display: 'flex', flexDirection: 'column', gap: '6px' }, children: [_jsx(Label, { htmlFor: "displayName", children: "Display Name" }), _jsx(Input, { id: "displayName", value: displayName, onChange: (e) => setDisplayName(e.target.value), placeholder: "Your name", maxLength: 64, autoFocus: true })] }), error && (_jsx("p", { style: { fontSize: '0.875rem', color: 'var(--color-danger)', fontFamily: 'var(--font-family-body)', marginTop: '8px' }, children: error })), _jsxs("div", { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '32px' }, children: [_jsx("button", { type: "button", onClick: handleSkipStep1, style: {
                                        background: 'none', border: 'none', cursor: 'pointer',
                                        fontSize: '0.875rem', color: 'var(--color-text-faint)',
                                        fontFamily: 'var(--font-family-body)',
                                    }, children: "Skip" }), _jsx(Button, { type: "submit", disabled: isSubmitting, children: isSubmitting ? 'Saving...' : 'Continue' })] })] })) : (_jsxs("form", { onSubmit: handleStep2Submit, children: [_jsx("h1", { style: {
                                fontFamily: 'var(--font-family-heading)',
                                fontSize: '1.5rem', fontWeight: 700,
                                color: 'var(--color-text)', marginBottom: '8px',
                            }, children: pendingInvite ? 'Create your own Watch Party' : 'Create your first Watch Party' }), _jsx("p", { style: { fontSize: '0.875rem', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-body)', marginBottom: '24px' }, children: pendingInvite ? (_jsxs(_Fragment, { children: ["You're about to join ", _jsx("strong", { children: pendingInvite.ownerDisplayName }), "'s party, ", _jsx("strong", { children: pendingInvite.partyName }), ". But first, every WatchParty member gets their own space \u2014 name yours below and you'll be on your way."] })) : ('This is your default group. You can invite friends and start building your watch list.') }), _jsxs("div", { style: { display: 'flex', flexDirection: 'column', gap: '6px' }, children: [_jsx(Label, { htmlFor: "partyName", children: "Party Name" }), _jsx(Input, { id: "partyName", value: partyName, onChange: (e) => setPartyName(e.target.value), placeholder: "My Watch Party", maxLength: 100, autoFocus: true })] }), error && (_jsx("p", { style: { fontSize: '0.875rem', color: 'var(--color-danger)', fontFamily: 'var(--font-family-body)', marginTop: '8px' }, children: error })), _jsxs("div", { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '32px' }, children: [_jsx("button", { type: "button", onClick: handleSkipStep2, disabled: isSubmitting, style: {
                                        background: 'none', border: 'none', cursor: 'pointer',
                                        fontSize: '0.875rem', color: 'var(--color-text-faint)',
                                        fontFamily: 'var(--font-family-body)',
                                    }, children: "Skip" }), _jsxs("div", { style: { display: 'flex', gap: '12px', alignItems: 'center' }, children: [isOAuthUser && (_jsx("button", { type: "button", onClick: () => { setStep(1); setError(null); }, style: {
                                                background: 'none', border: 'none', cursor: 'pointer',
                                                fontSize: '0.875rem', color: 'var(--color-text-muted)',
                                                fontFamily: 'var(--font-family-body)',
                                            }, children: "Back" })), _jsx(Button, { type: "submit", disabled: isSubmitting, children: isSubmitting ? 'Creating...' : "Let's go" })] })] })] })) })] }));
}
